import React, { useState, useEffect, useContext, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faTimes } from "@fortawesome/free-solid-svg-icons";
import { SpaceContext } from "../../SpaceContext";
import service from "../../utils/request";
import message from "../../utils/message";
import useTopicMatcher from "./useTopicMatcher";
import KeywordSignalView from "./KeywordSignalView";

export default function CheckInModal({ isOpen, onClose, item, items, setFileList, onSuccess }) {
    const [policies, setPolicies] = useState([]);
    const [allTopics, setAllTopics] = useState([]);
    const [assignedTopicsMap, setAssignedTopicsMap] = useState({});
    const [selectedPolicyId, setSelectedPolicyId] = useState("");
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // State for files
    const [fileNames, setFileNames] = useState({});
    const [originalFileNames, setOriginalFileNames] = useState({});
    const [fileKeywords, setFileKeywords] = useState({});
    const [originalFileKeywords, setOriginalFileKeywords] = useState({});
    const [activeFileId, setActiveFileId] = useState("");

    const spaceContext = useContext(SpaceContext);

    // Normalize input item/items to array of non-folder items
    const itemList = useMemo(() => {
        let rawList = [];
        if (items) {
            rawList = Array.isArray(items) ? items : Object.values(items);
        } else if (item) {
            rawList = [item];
        }
        return rawList.filter(i => i && !i.is_folder);
    }, [items, item]);

    const fileIds = useMemo(() => Object.keys(fileNames), [fileNames]);

    useEffect(() => {
        if (isOpen) {
            fetchInitialData();
            const curr_path = spaceContext?.getCurrentFolderPath ? spaceContext.getCurrentFolderPath() : "";
            const initialNames = {};
            const initialOriginals = {};
            const initialKw = {};
            const initialOriginalKw = {};

            itemList.forEach(i => {
                const id = i.item_id;
                const name = i.item_name || "";
                initialNames[id] = name;
                initialOriginals[id] = name;

                // Use existing keywords if available, else generate standard 2-line format
                const defaultKw = i.keywords || (curr_path ? `${name}\n${curr_path}` : name);
                initialKw[id] = defaultKw;
                initialOriginalKw[id] = defaultKw;
            });

            setFileNames(initialNames);
            setOriginalFileNames(initialOriginals);
            setFileKeywords(initialKw);
            setOriginalFileKeywords(initialOriginalKw);

            if (itemList.length > 0) {
                setActiveFileId(itemList[0].item_id);
            }
        }
    }, [isOpen]);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [policiesRes, topicsRes, ...itemTopicsRes] = await Promise.allSettled([
                service.get("/api/policy/file_policy"),
                service.get("/api/topic/"),
                ...itemList.map(it => service.get(`/api/topic/topics_by_file?item_id=${it.item_id}`))
            ]);

            if (policiesRes.status === "fulfilled" && policiesRes.value) {
                const processedPolicies = (policiesRes.value || []).map(p => ({ id: p.key, name: p.value }));
                processedPolicies.sort((a, b) => a.name.localeCompare(b.name));
                setPolicies(processedPolicies);
                if (processedPolicies.length > 0) {
                    setSelectedPolicyId(processedPolicies[0].id);
                }
            }

            if (topicsRes.status === "fulfilled" && topicsRes.value) {
                setAllTopics(topicsRes.value || []);
            }

            const newAssignedMap = {};
            itemList.forEach((it, idx) => {
                const res = itemTopicsRes[idx];
                if (res && res.status === "fulfilled" && Array.isArray(res.value)) {
                    newAssignedMap[it.item_id] = res.value;
                } else {
                    newAssignedMap[it.item_id] = [];
                }
            });
            setAssignedTopicsMap(newAssignedMap);
        } catch (err) {
            console.error("Error loading checkin metadata:", err);
            message("error", "Failed to load checkin policies");
        } finally {
            setLoading(false);
        }
    };

    // Resolve assigned topics for active file (from DB)
    const assignedList = useMemo(() => {
        if (!activeFileId) return [];
        const raw = assignedTopicsMap[activeFileId] || [];
        return raw.map(t => {
            const found = allTopics.find(
                at => at.id === t.topic_id || at.original_topic_id === t.topic_id || at.name?.toLowerCase() === t.topic_name?.toLowerCase()
            );
            return {
                id: t.topic_id,
                name: t.topic_name || found?.name || "Unknown",
                context: found?.context || "Topic",
                isAssigned: true,
            };
        });
    }, [assignedTopicsMap, activeFileId, allTopics]);

    // Active file keywords & realtime topic matching
    const curr_path = spaceContext?.getCurrentFolderPath ? spaceContext.getCurrentFolderPath() : "";
    const currentActiveKeywords = fileKeywords[activeFileId] ?? "";
    const currentActiveName = fileNames[activeFileId] ?? "";

    const matchSourceText = useMemo(() => {
        return `${currentActiveName}\n${currentActiveKeywords}\n${curr_path}`;
    }, [currentActiveName, currentActiveKeywords, curr_path]);

    const { matchedTopics } = useTopicMatcher(matchSourceText, allTopics);

    // Resolve preview topics (matched from current text, not yet in DB)
    const previewList = useMemo(() => {
        const assignedIdSet = new Set(assignedList.map(a => a.id));
        const assignedNameSet = new Set(assignedList.map(a => a.name?.toLowerCase()));

        return (matchedTopics || [])
            .filter(t => !assignedIdSet.has(t.id) && !assignedIdSet.has(t.original_topic_id) && !assignedNameSet.has(t.name?.toLowerCase()))
            .map(t => {
                const orig = t.original_topic_id ? allTopics.find(at => at.id === t.original_topic_id) : t;
                return {
                    ...t,
                    name: orig?.name,
                    isAssigned: false,
                };
            });
    }, [matchedTopics, assignedList, allTopics]);

    // Two-way sync: Handle Filename input change -> updates Line 1 of Keywords
    const handleFileNameChange = (val) => {
        if (!activeFileId) return;
        setFileNames(prev => ({ ...prev, [activeFileId]: val }));

        setFileKeywords(prev => {
            const currentKw = prev[activeFileId] || "";
            const lines = currentKw.split("\n");
            if (lines.length > 1) {
                return { ...prev, [activeFileId]: `${val}\n${lines.slice(1).join("\n")}` };
            }
            return { ...prev, [activeFileId]: val };
        });
    };

    // Two-way sync: Handle Keywords editor change -> updates Filename from Line 1
    const handleKeywordsChange = (val) => {
        if (!activeFileId) return;
        setFileKeywords(prev => ({ ...prev, [activeFileId]: val }));

        const firstLine = val.split("\n")[0] || "";
        const cleanName = firstLine.replace(/\[\[(.*?)(?:\|(.*?))?\]\]/g, "$1").trim();
        if (cleanName) {
            setFileNames(prev => ({ ...prev, [activeFileId]: cleanName }));
        }
    };

    const handleCheckIn = async () => {
        if (!selectedPolicyId) {
            message("error", "Please select a policy");
            return;
        }
        if (fileIds.length === 0) {
            message("error", "No valid files selected for check-in");
            return;
        }

        // Validate that no file name is empty
        for (const id of fileIds) {
            const currentName = fileNames[id]?.trim();
            if (!currentName) {
                message("error", "File name cannot be empty");
                return;
            }
        }

        setActionLoading(true);
        message("loading", "Checking in file(s) to library...");
        try {
            // 1. Rename and persist keywords to DB for any modified files
            const currentNames = { ...fileNames };
            for (const id of fileIds) {
                const newName = fileNames[id]?.trim();
                const originalName = originalFileNames[id];
                const currentKw = fileKeywords[id];
                const originalKw = originalFileKeywords[id];

                const hasNameChanged = newName && newName !== originalName;
                const hasKeywordsChanged = currentKw !== undefined && currentKw !== originalKw;

                if (hasNameChanged || hasKeywordsChanged) {
                    const renamePayload = { item_id: id };
                    if (newName) renamePayload.new_name = newName;
                    if (currentKw !== undefined) renamePayload.keywords = currentKw;

                    const renameRes = await service.post("/filer/rename", renamePayload);
                    const resName = renameRes?.new_name || newName;
                    const resKw = renameRes?.keywords ?? currentKw;

                    currentNames[id] = resName;
                    setFileNames(prev => ({ ...prev, [id]: resName }));
                    setOriginalFileNames(prev => ({ ...prev, [id]: resName }));
                    setFileKeywords(prev => ({ ...prev, [id]: resKw }));
                    setOriginalFileKeywords(prev => ({ ...prev, [id]: resKw }));

                    // Update parent references
                    const foundItem = itemList.find(it => it.item_id === id);
                    if (foundItem) {
                        foundItem.item_name = resName;
                        foundItem.keywords = resKw;
                    }
                    if (item && item.item_id === id) {
                        item.item_name = resName;
                        item.keywords = resKw;
                    }

                    if (setFileList) {
                        setFileList(prevFileList =>
                            prevFileList.map(it =>
                                it.item_id === id
                                    ? { ...it, item_name: resName, keywords: resKw }
                                    : it
                            )
                        );
                    }
                }
            }

            // 2. Call check-in API (server reads updated keywords from DB directly)
            const curr_path = spaceContext?.getCurrentFolderPath ? spaceContext.getCurrentFolderPath() : "";
            const res = await service.post(
                `/api/library/checkin`,
                {
                    item_ids: fileIds,
                    policy_id: selectedPolicyId,
                    curr_path: curr_path
                }
            );

            const successIds = res?.success || [];
            const failedIds = res?.failed || [];
            const skippedIds = res?.skipped || [];

            if (successIds.length > 0 && onSuccess) {
                onSuccess(selectedPolicyId, successIds, currentNames);
            }

            // Keep only failed files in fileNames, remove successful & skipped files
            const failedSet = new Set(failedIds);
            setFileNames(prev => {
                const next = {};
                Object.entries(prev).forEach(([id, name]) => {
                    if (failedSet.has(id)) next[id] = name;
                });
                return next;
            });
            setOriginalFileNames(prev => {
                const next = {};
                Object.entries(prev).forEach(([id, name]) => {
                    if (failedSet.has(id)) next[id] = name;
                });
                return next;
            });
            setFileKeywords(prev => {
                const next = {};
                Object.entries(prev).forEach(([id, kw]) => {
                    if (failedSet.has(id)) next[id] = kw;
                });
                return next;
            });
            setOriginalFileKeywords(prev => {
                const next = {};
                Object.entries(prev).forEach(([id, kw]) => {
                    if (failedSet.has(id)) next[id] = kw;
                });
                return next;
            });

            if (failedIds.length === 0) {
                if (skippedIds.length > 0 && successIds.length === 0) {
                    message("error", "You can only check in files that you own.");
                    onClose();
                } else if (skippedIds.length > 0) {
                    message("success", `Checked in ${successIds.length} ${successIds.length === 1 ? 'file' : 'files'} successfully, skipped ${skippedIds.length} ${skippedIds.length === 1 ? 'file' : 'files'} (not owner).`);
                    onClose();
                } else {
                    message("success", `Checked in ${successIds.length} ${successIds.length === 1 ? 'file' : 'files'} successfully!`);
                    onClose();
                }
            } else {
                if (successIds.length > 0) {
                    message("warn", `Checked in ${successIds.length} file(s). Cannot find any topics for ${failedIds.length} file(s), please rename/edit keywords to check in.`);
                } else {
                    message("error", "Cannot find any topics, please edit filename or keywords to match available topics.");
                }
                // Focus on first failed file
                if (failedIds[0]) {
                    setActiveFileId(failedIds[0]);
                }
            }
        } catch (err) {
            console.error("Check-in failed:", err);
            message("error", err?.message || "Failed to check in file");
        } finally {
            setActionLoading(false);
        }
    };

    if (!isOpen) return null;

    const renderFileSwitcher = () => {
        if (fileIds.length === 0) {
            return (
                <div className="text-start">
                    <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-1">File</span>
                    <div className="text-sm font-medium text-zinc-400 bg-zinc-50 px-3 py-2 rounded-lg border border-zinc-100">
                        No files selected
                    </div>
                </div>
            );
        }

        if (fileIds.length === 1) {
            const singleId = fileIds[0];
            return (
                <div className="text-start">
                    <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-1">
                        File Name
                    </label>
                    <input
                        type="text"
                        value={fileNames[singleId] ?? ""}
                        onChange={(e) => handleFileNameChange(e.target.value)}
                        disabled={actionLoading}
                        className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none bg-white text-zinc-800 focus:border-[#FF8C39] focus:ring-1 focus:ring-[#FF8C39] transition-all font-medium disabled:bg-zinc-100 disabled:cursor-not-allowed"
                        placeholder="Enter file name"
                    />
                </div>
            );
        }

        const activeIndex = fileIds.indexOf(activeFileId);

        return (
            <div className="text-start space-y-1.5">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        Files ({fileIds.length} selected)
                    </span>
                    <span className="text-[11px] text-zinc-400 font-medium">
                        Editing {activeIndex >= 0 ? activeIndex + 1 : 1} of {fileIds.length}
                    </span>
                </div>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={fileNames[activeFileId] ?? ""}
                        onChange={(e) => handleFileNameChange(e.target.value)}
                        disabled={actionLoading}
                        className="flex-1 px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none bg-white text-zinc-800 focus:border-[#FF8C39] focus:ring-1 focus:ring-[#FF8C39] transition-all font-medium disabled:bg-zinc-100 disabled:cursor-not-allowed"
                        placeholder="Enter file name"
                    />
                    <select
                        value={activeFileId}
                        onChange={(e) => setActiveFileId(e.target.value)}
                        disabled={actionLoading}
                        className="w-36 px-2.5 py-2 text-xs border border-zinc-200 rounded-lg outline-none bg-zinc-50 hover:bg-zinc-100 text-zinc-700 focus:border-[#FF8C39] focus:ring-1 focus:ring-[#FF8C39] transition-all cursor-pointer font-medium truncate"
                        title="Switch active file"
                    >
                        {fileIds.map((id, idx) => (
                            <option key={id} value={id}>
                                {idx + 1}. {fileNames[id] || "Unnamed file"}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-all duration-300"
            onClick={onClose}
            onDoubleClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
        >
            <div
                className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl border border-zinc-200 overflow-hidden transform transition-all duration-300 flex flex-col max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 bg-zinc-50/50">
                    <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faBook} className="text-[#FF8C39] w-4 h-4" />
                        <h3 className="text-lg font-semibold text-zinc-950">Check in</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 transition-colors"
                    >
                        <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                    {/* 1. File Switcher & Input */}
                    {renderFileSwitcher()}

                    {/* 2. Keywords Input with Inline Chip Badges (managed via KeywordSignalView) */}
                    {activeFileId && (
                        <div className="text-start space-y-1">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block">
                                    Keywords
                                </label>
                                {matchedTopics.length > 0 && (
                                    <span className="text-[11px] font-medium text-[#FF8C39]">
                                        {matchedTopics.length} matched
                                    </span>
                                )}
                            </div>
                            <KeywordSignalView
                                value={fileKeywords[activeFileId] ?? ""}
                                onChange={handleKeywordsChange}
                                matchedTopics={matchedTopics}
                                disabled={actionLoading}
                            />
                        </div>
                    )}

                    {/* 3. Policy Selection */}
                    {loading ? (
                        <div className="py-4 flex flex-col items-center justify-center gap-2">
                            <div className="w-6 h-6 border-2 border-[#FF8C39] border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-xs text-zinc-500 font-medium">Loading policies...</span>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-1.5 text-start">
                            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Policy</label>
                            <select
                                value={selectedPolicyId}
                                onChange={(e) => setSelectedPolicyId(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none bg-white text-zinc-800 focus:border-[#FF8C39] focus:ring-1 focus:ring-[#FF8C39] transition-all cursor-pointer font-medium"
                            >
                                {policies.map((policy) => (
                                    <option key={policy.id} value={policy.id}>
                                        {policy.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* 4. Topics: Assigned (solid orange border) & Preview (dashed orange border, NO PREVIEW text) */}
                    <div className="text-start space-y-1.5">
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                                Topics
                            </label>
                        </div>

                        <div className="p-3 rounded-lg bg-zinc-50/80 border border-zinc-200/80 min-h-[46px] flex flex-wrap items-center gap-2">
                            {assignedList.map((t) => (
                                <span
                                    key={`assigned-${t.id}`}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50 text-zinc-900 border border-[#FF8C39] shadow-2xs"
                                    title="Assigned topic in database"
                                >
                                    <span className="text-[10px] text-amber-700 font-normal">{t.context}:</span>
                                    <span className="font-semibold">{t.name}</span>
                                </span>
                            ))}

                            {previewList.map((t) => (
                                <span
                                    key={`preview-${t.id}`}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-amber-50/30 text-zinc-900 border-2 border border-[#FF8C39] shadow-2xs"
                                    title="Preview topic (detected from keywords/filename)"
                                >
                                    <span className="font-semibold">{t.name}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-100 bg-zinc-50/50">
                    <button
                        onClick={onClose}
                        disabled={actionLoading}
                        className="px-4 py-2 text-sm font-semibold text-zinc-600 hover:text-zinc-800 hover:bg-zinc-100 rounded-lg transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCheckIn}
                        disabled={actionLoading || loading || fileIds.length === 0}
                        className="px-4 py-2 text-sm font-semibold text-white bg-[#FF8C39] hover:bg-[#e67a2f] disabled:bg-zinc-300 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all flex items-center justify-center min-w-[100px]"
                    >
                        {actionLoading ? "Checking In..." : "Check In"}
                    </button>
                </div>
            </div>
        </div>
    );
}
